// Generate a downloadable, comprehensive, plain-language user guide for
// the S1000D Cost Estimator using jsPDF.  Written in Australian English
// throughout (organise, authorise, centre, behaviour, utilisation, etc.).
// Screenshots are embedded inline from /guide-assets/*.jpg where they help.
import jsPDF from "jspdf";

const MARGIN_X = 18;
const MARGIN_TOP = 22;
const MARGIN_BOTTOM = 22;
const A4_W = 210;
const A4_H = 297;
const CONTENT_W = A4_W - MARGIN_X * 2;
const LINE_H = 5.2;
const PURPLE = "#6B21A8";
const PURPLE_LIGHT = "#E9D5FF";
const DARK = "#1E293B";
const GREY = "#64748B";
const LIGHT_GREY = "#9CA3AF";
const AMBER = "#CA8A04";
const RED = "#FF2400";
const TIP_BG = "#FEF9C3";
const TIP_BORDER = "#CA8A04";

// Fetch an asset URL and convert to a data URL jsPDF can consume.
async function loadImage(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to fetch ${url}`);
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}

// Decode the image to figure out its intrinsic aspect ratio so we can fit
// it neatly into the page without distortion.
function loadImageDims(dataUrl) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
    img.onerror = () => resolve({ w: 1600, h: 900 });
    img.src = dataUrl;
  });
}

class GuideDoc {
  constructor() {
    this.doc = new jsPDF({ unit: "mm", format: "a4" });
    this.y = MARGIN_TOP;
    this.page = 1;
  }

  newPage() {
    this.doc.addPage();
    this.page += 1;
    this.y = MARGIN_TOP;
    this._drawFooter();
    this._drawHeaderBand();
  }

  ensureSpace(needed) {
    if (this.y + needed > A4_H - MARGIN_BOTTOM) this.newPage();
  }

  _drawFooter() {
    const d = this.doc;
    d.setDrawColor(PURPLE_LIGHT);
    d.setLineWidth(0.3);
    d.line(MARGIN_X, A4_H - 15, A4_W - MARGIN_X, A4_H - 15);
    d.setFont("helvetica", "normal");
    d.setFontSize(8);
    d.setTextColor(GREY);
    d.text("S1000D Cost Estimator — User Guide", MARGIN_X, A4_H - 10);
  }

  _drawHeaderBand() {
    if (this.page === 1) return;
    const d = this.doc;
    // Thin purple line under header
    d.setDrawColor(PURPLE_LIGHT);
    d.setLineWidth(0.3);
    d.line(MARGIN_X, 14, A4_W - MARGIN_X, 14);
    // Left: guide title
    d.setFont("helvetica", "normal");
    d.setFontSize(8);
    d.setTextColor(GREY);
    d.text("S1000D Cost Estimator — User Guide", MARGIN_X, 11);
    // Right: company name
    d.text("TIM Consulting Pty Ltd", A4_W - MARGIN_X, 11, { align: "right" });
    this.y = Math.max(this.y, 20);
  }

  h1(text) {
    this.ensureSpace(22);
    const d = this.doc;
    d.setFont("helvetica", "bold");
    d.setFontSize(20);
    d.setTextColor(PURPLE);
    d.text(text, MARGIN_X, this.y + 8);
    d.setDrawColor(PURPLE);
    d.setLineWidth(0.8);
    d.line(MARGIN_X, this.y + 11, MARGIN_X + 30, this.y + 11);
    this.y += 17;
  }

  h2(text) {
    this.ensureSpace(13);
    const d = this.doc;
    d.setFont("helvetica", "bold");
    d.setFontSize(13);
    d.setTextColor(DARK);
    d.text(text, MARGIN_X, this.y + 6);
    this.y += 9;
  }

  h3(text) {
    this.ensureSpace(9);
    const d = this.doc;
    d.setFont("helvetica", "bold");
    d.setFontSize(10.5);
    d.setTextColor(PURPLE);
    d.text(text, MARGIN_X, this.y + 5);
    this.y += 7;
  }

  para(text, opts = {}) {
    const d = this.doc;
    d.setFont("helvetica", opts.italic ? "italic" : "normal");
    d.setFontSize(opts.size || 9.5);
    d.setTextColor(opts.color || DARK);
    const lines = d.splitTextToSize(text, CONTENT_W);
    for (const line of lines) {
      this.ensureSpace(LINE_H);
      d.text(line, MARGIN_X, this.y + 3.5);
      this.y += LINE_H;
    }
    this.y += 1.2;
  }

  bullets(items) {
    const d = this.doc;
    d.setFont("helvetica", "normal");
    d.setFontSize(9.5);
    d.setTextColor(DARK);
    for (const it of items) {
      const lines = d.splitTextToSize(it, CONTENT_W - 6);
      lines.forEach((line, i) => {
        this.ensureSpace(LINE_H);
        if (i === 0) {
          d.setTextColor(PURPLE);
          d.text("•", MARGIN_X + 1, this.y + 3.5);
          d.setTextColor(DARK);
        }
        d.text(line, MARGIN_X + 6, this.y + 3.5);
        this.y += LINE_H;
      });
    }
    this.y += 1.5;
  }

  callout(label, body, kind = "tip") {
    const d = this.doc;
    const color = kind === "warn" ? RED : kind === "amber" ? AMBER : kind === "note" ? GREY : PURPLE;
    const bg = kind === "warn" ? "#FEF2F2" : kind === "amber" ? "#FFFBEB" : kind === "note" ? "#F3F4F6" : TIP_BG;
    d.setFont("helvetica", "normal");
    d.setFontSize(9);
    const lines = d.splitTextToSize(body, CONTENT_W - 8);
    const h = 6 + lines.length * LINE_H;
    this.ensureSpace(h + 2);
    d.setFillColor(bg);
    d.rect(MARGIN_X, this.y, CONTENT_W, h, "F");
    d.setDrawColor(color);
    d.setLineWidth(0.8);
    d.line(MARGIN_X, this.y, MARGIN_X, this.y + h);
    d.setFont("helvetica", "bold");
    d.setFontSize(8);
    d.setTextColor(color);
    d.text(label.toUpperCase(), MARGIN_X + 4, this.y + 5);
    d.setFont("helvetica", "normal");
    d.setTextColor(DARK);
    let by = this.y + 9.5;
    for (const line of lines) {
      d.text(line, MARGIN_X + 4, by);
      by += LINE_H;
    }
    this.y += h + 3;
  }

  kbdTable(rows, colA = 60) {
    const d = this.doc;
    const colB = CONTENT_W - colA;
    d.setFont("helvetica", "bold");
    d.setFontSize(9);
    d.setTextColor("#FFFFFF");
    d.setFillColor(DARK);
    this.ensureSpace(6);
    d.rect(MARGIN_X, this.y, CONTENT_W, 6, "F");
    d.text("Item", MARGIN_X + 2, this.y + 4);
    d.text("What it does", MARGIN_X + colA + 2, this.y + 4);
    this.y += 6;
    d.setTextColor(DARK);
    d.setFont("helvetica", "normal");
    for (let i = 0; i < rows.length; i++) {
      const [a, b] = rows[i];
      const linesA = d.splitTextToSize(a, colA - 4);
      const linesB = d.splitTextToSize(b, colB - 4);
      const rowH = Math.max(linesA.length, linesB.length) * LINE_H + 1;
      this.ensureSpace(rowH);
      if (i % 2 === 0) {
        d.setFillColor("#F9FAFB");
        d.rect(MARGIN_X, this.y, CONTENT_W, rowH, "F");
      }
      d.setFont("helvetica", "bold");
      linesA.forEach((ln, idx) => d.text(ln, MARGIN_X + 2, this.y + 3.5 + idx * LINE_H));
      d.setFont("helvetica", "normal");
      linesB.forEach((ln, idx) => d.text(ln, MARGIN_X + colA + 2, this.y + 3.5 + idx * LINE_H));
      this.y += rowH;
    }
    this.y += 3;
  }

  // Embed an image filling the content width, preserving aspect ratio. If it
  // won't fit on the current page, start a new one. Caption renders underneath.
  async image(url, caption) {
    let dataUrl;
    try { dataUrl = await loadImage(url); }
    catch { return; /* skip silently if asset missing */ }
    const { w, h } = await loadImageDims(dataUrl);
    const drawW = CONTENT_W;
    const drawH = (drawW * h) / w;
    this.ensureSpace(drawH + 8);
    this.doc.setDrawColor("#D1D5DB");
    this.doc.setLineWidth(0.3);
    this.doc.rect(MARGIN_X, this.y, drawW, drawH);
    this.doc.addImage(dataUrl, "JPEG", MARGIN_X, this.y, drawW, drawH);
    this.y += drawH + 1.5;
    if (caption) {
      this.doc.setFont("helvetica", "italic");
      this.doc.setFontSize(8);
      this.doc.setTextColor(GREY);
      this.doc.text(`Screenshot: ${caption}`, MARGIN_X, this.y + 3);
      this.y += 6;
    }
  }

  finalise(filename) {
    const total = this.doc.getNumberOfPages();
    for (let p = 1; p <= total; p++) {
      this.doc.setPage(p);
      this.doc.setFont("helvetica", "normal");
      this.doc.setFontSize(8);
      this.doc.setTextColor(GREY);
      this.doc.text(`Page ${p}`,
        A4_W - MARGIN_X, A4_H - 10, { align: "right" });
    }
    this.doc.save(filename);
  }
}

// ============================================================================
// Content (Australian English)
// ============================================================================
export async function downloadUserGuide() {
  const g = new GuideDoc();
  const d = g.doc;

  // ------ Cover ------
  // Match the MySpecWorkspace main guide cover presentation
  // TIM Consulting logo centred at top
  let logoData;
  try { logoData = await loadImage("/guide-assets/tim_consulting_logo.jpg"); }
  catch { /* logo missing — skip */ }
  if (logoData) {
    const { w: lw, h: lh } = await loadImageDims(logoData);
    const logoW = 60;
    const logoH = (logoW * lh) / lw;
    d.addImage(logoData, "JPEG", (A4_W - logoW) / 2, 40, logoW, logoH);
    g.y = 40 + logoH + 12;
  } else {
    g.y = 80;
  }
  // Title
  d.setFont("helvetica", "bold");
  d.setFontSize(28);
  d.setTextColor(DARK);
  const title1 = "S1000D Cost Estimator";
  d.text(title1, A4_W / 2, g.y, { align: "center" });
  g.y += 12;
  d.setFontSize(28);
  d.text("User Guide", A4_W / 2, g.y, { align: "center" });
  g.y += 16;
  // Subtitle
  d.setFont("helvetica", "normal");
  d.setFontSize(14);
  d.setTextColor(GREY);
  d.text("A comprehensive plain-English walkthrough for bid managers,", A4_W / 2, g.y, { align: "center" });
  g.y += 6;
  d.text("S1000D specialists and technical authors.", A4_W / 2, g.y, { align: "center" });
  g.y += 20;
  // Version and company
  d.setFontSize(10);
  d.setTextColor(GREY);
  const dateStr = new Date().toLocaleDateString("en-AU", { day: "2-digit", month: "long", year: "numeric" });
  d.text(`Version 2.0  |  ${dateStr}`, A4_W / 2, g.y, { align: "center" });
  g.y += 6;
  d.text("TIM Consulting Pty Ltd", A4_W / 2, g.y, { align: "center" });
  g.y += 12;

  g.h2("What this guide covers");
  g.para(
    "This guide explains how to use the S1000D Cost Estimator from your first " +
    "blank project through to handing over a complete bid pack. Every tab and " +
    "every function in the app is documented, with screenshots where they help."
  );
  g.bullets([
    "Chapter 1 — Getting started, top bar, tabs, save / load and the help system.",
    "Chapter 2 — Building the scope: System Breakdown Structure, Data Modules, Illustrations, DML / SNS, BR / BREX.",
    "Chapter 3 — Planning the work: Roles & Rates, Rate Cards (with approval workflow), Resourcing, Schedule.",
    "Chapter 4 — Preparing the bid: Risk, Bid Pricing, Summary, Readiness, Seed Pack and Bid Pack exports.",
    "Chapter 5 — Library: Compare, Portfolio, Saved Projects.",
    "Chapter 6 — FX rates, currency support and the 1:1 fallback story.",
    "Chapter 7 — Importing data: DM matrix, SBS and Risk register importers.",
    "Appendices — Glossary, FAQ, full tab cheat sheet and keyboard / data-testid reference.",
  ]);
  g.callout("Plain-language note",
    "Words like 'authorise', 'organise', 'centre', 'behaviour' and 'utilisation' are spelt the " +
    "Australian way throughout this guide. The application itself shows whichever spelling " +
    "matches the language you choose in the top-right corner — English, French, German, Spanish or Italian.");

  // ---------- Chapter 1 ----------
  g.newPage();
  g.h1("1. Getting started");

  g.h2("Opening the estimator");
  g.para(
    "Open the app in any modern web browser (Chrome, Edge, Firefox, Safari). " +
    "You do not need to sign in. Every estimate is saved on the server by name. " +
    "The first screen shows an empty project on the Data Modules tab; from there " +
    "you can build your estimate up using the tabs across the top."
  );

  g.h2("The top bar");
  await g.image("/guide-assets/header.jpg", "the application header, showing project name, currency selector, FX status pill, language switcher and the main action buttons.");
  g.kbdTable([
    ["Project name", "Give your estimate a clear name. The name appears in every export (CSV, Excel, PDF, ZIP) and in the Saved Projects list."],
    ["Currency selector", "Choose from 110 ISO 4217 currencies grouped into 'FX-supported (live rates)' and 'All other currencies'. Live FX is provided by frankfurter.dev for 31 major currencies."],
    ["FX status pill", "Green 'FX LIVE' dot when ECB rates are available for the chosen currency; grey 'FX 1:1' when the Compare tab will fall back to identity conversion."],
    ["Tags", "Type a tag (for example 'defence', 'rotary' or 'navy') and press Enter to add it. Tags help you filter the Saved Projects list and the Portfolio dashboard."],
    ["Total project cost", "Live running total including overhead, contingency and BREX cost (when 'Include in project total' is enabled in BREX)."],
    ["Language switcher", "English, French, German, Spanish or Italian. Numbers, currency formatting and dates all follow the selected locale (for example 1.234,56 in German)."],
    ["Guide button", "Opens this user guide as an in-app slide-over drawer. The drawer has its own 'PDF' button to download this very document."],
    ["New", "Clears the workspace to start a fresh project. Prompts before discarding unsaved changes."],
    ["Duplicate", "Copies the current estimate, including all tabs, into a new draft. Useful for variant pricing."],
    ["Save", "Persists the project to the database. First save creates a record; further saves overwrite and append to the revision history."],
    ["Share", "Copies a read-only URL of the current estimate to the clipboard so colleagues can view it."],
    ["CSV / XLSX / PDF", "Export the active estimate. The PDF is the polished hand-out copy; CSV and XLSX are aimed at downstream analysis."],
  ]);

  g.h2("Tab navigation");
  g.para(
    "The estimator organises its work into four groups of tabs across the top:"
  );
  g.bullets([
    "Scope — System Breakdown, Data Modules, Illustrations, DML / SNS, BR / BREX.",
    "Plan — Roles & Rates, Rate Cards, Resourcing, Schedule.",
    "Bid — Risk, Bid Pricing, Summary, Readiness.",
    "Library — Compare, Portfolio, Saved Projects.",
  ]);
  g.para(
    "Click any group label to switch to its first sub-tab, or click a sub-tab " +
    "directly. The grey bar under the group buttons shows which sub-tab is active. " +
    "Every tab carries a small '?' icon next to its title that opens this user guide " +
    "pre-scrolled to the relevant section."
  );

  g.h2("Saving, loading and revisions");
  g.para(
    "Click Save to persist the current estimate. The first save creates a new " +
    "record; subsequent saves overwrite the same record and append a snapshot to " +
    "the revision history. Open Library → Saved Projects to load, duplicate, " +
    "delete or restore any earlier revision through the History modal. The number " +
    "of revisions kept is unlimited."
  );
  g.callout("Tip",
    "Save early and save often. Every Save snapshots the previous state into the " +
    "revision log so you can roll back to any point in time without losing the work " +
    "you have done since the last save.");

  // ---------- Chapter 2 ----------
  g.newPage();
  g.h1("2. Building the scope");

  g.h2("2.1 System Breakdown Structure (SBS)");
  g.para(
    "The SBS tab is the recommended starting point. You describe the product as " +
    "a tree of Systems, Sub-Systems and Sub-Subsystems, then tell the estimator " +
    "how many General items and Repairable items live at each level. The tree " +
    "drives the rest of the estimate: it pre-populates the Data Modules matrix, " +
    "generates the DMC inventory and pre-seeds applicability into the BREX skeleton."
  );
  await g.image("/guide-assets/sbs.jpg", "the System Breakdown Structure tab with a two-system tree and the DMC inventory preview underneath.");

  g.h3("Building the tree step by step");
  g.bullets([
    "Click 'Add System' to create a top-level entry — name it something like 'Hydraulics' or 'Avionics'.",
    "Use the '+ Add Sub-System' button on a system row to nest children. Repeat for Sub-Subsystems.",
    "Fill in the General qty and Repairable qty columns for every node that has items.",
    "Pick the General DM type and Repairable DM type at the top of the tab (defaults are 040 Descriptive and 520 Procedural).",
    "Set the hours-per-item for the Simple, Intermediate and Complex levels (defaults 6 / 8 / 12 hours).",
    "Use the chevron icons on each row to collapse or expand branches.",
    "The bin icon removes the node and any children below it.",
  ]);
  g.h3("Complexity mapping");
  g.para(
    "Systems map to Simple complexity, Sub-Systems to Intermediate, and Sub-Subsystems " +
    "to Complex. General-item quantities go into the General DM type row; Repairable-item " +
    "quantities go into the Repairable DM type row. The roll-up cards at the bottom of the " +
    "tab show you the totals that will be written into the DM matrix if you sync."
  );
  g.h3("Spreadsheet import");
  g.para(
    "Click 'Template' to download a starter workbook with the right column headings. " +
    "The expected columns are System, Sub-System, Sub-Subsystem, General and Repairable. " +
    "Blank Sub or Sub-Sub cells mean the row applies to its parent level. The importer " +
    "is header-aware (case-insensitive) and de-duplicates names automatically. Choose " +
    "Replace (overwrite the current tree) or Merge (accumulate into existing names) " +
    "before you upload. Warnings such as 'duplicate System merged' or 'row had zero " +
    "quantities' appear in an amber panel under the result line, with a count badge."
  );
  g.h3("Sync to DM matrix");
  g.para(
    "Click 'Sync to DM Matrix (Replace)' to overwrite the quantities for the chosen " +
    "DM types across the three complexity levels. 'Sync to DM Matrix (Merge)' adds to " +
    "the existing quantities instead. The hours-per-item values are also written into " +
    "the matching DM matrix cells when you sync in Replace mode."
  );
  g.h3("DMC inventory and Seed Pack");
  g.para(
    "Once your tree has at least one item, the DMC Inventory section near the bottom " +
    "of the SBS tab lists ready-made S1000D-style DMCs (for example " +
    "'PROJECT-A-21-10-00-00-0001-040A-D'). SNS codes are allocated positionally from " +
    "the active SNS preset chosen on the DML / SNS tab; missing codes fall back to '00'. " +
    "Click 'Download DMC inventory (CSV)' for the full list, or — preferably — include " +
    "it automatically by exporting the CSDB Seed Pack from the Readiness tab."
  );
  g.callout("Note",
    "Pick an SNS preset that matches your product family (Aircraft ATA-100, Helicopter, " +
    "Land Vehicle, Surface Ship or Munitions) for the cleanest DMC allocation. The Generic " +
    "preset gives '00' for everything below the system level.");

  g.newPage();
  g.h2("2.2 Data Modules");
  g.para(
    "The Data Modules tab is the heart of the estimate. Every S1000D data module type " +
    "(Descriptive, Procedural, Wiring, Schematic, Crew, Operator, Maintenance, " +
    "Fault Isolation, Process, Service Bulletin, Illustrated Parts and more) has its " +
    "own row with a quantity and hours-per-unit input at three complexity bands."
  );
  await g.image("/guide-assets/datamodules.jpg", "the Data Modules matrix — quantity and hours per unit at Simple / Intermediate / Complex bands.");
  g.bullets([
    "Quantities default to zero — fill in only the rows that apply to your contract.",
    "Hours-per-unit start at the indicative 6 / 8 / 12 figures but every cell is editable.",
    "Row totals (right edge) and column totals (bottom) update as you type.",
    "Use the 'Import' button to bring in a CSV or XLSX with the same shape as the export. Replace overwrites the matrix; Merge adds to existing quantities.",
    "Leading zeros in DM codes (such as 040) are preserved when importing — there is no need to wrap them in quotes in the source file.",
    "Excluding a row entirely: set every cell in that row to zero. The row stays in place for reference but contributes nothing to totals.",
  ]);

  g.h2("2.3 Illustrations");
  g.para(
    "Illustrations have their own table at three complexity bands: Simple line " +
    "drawings, Intermediate exploded views and Complex 3D / cut-away renders. Set " +
    "the quantity and the hours per illustration in each band. The blended rate " +
    "from the Roles tab applies to these hours when costing the project."
  );

  g.h2("2.4 DML / SNS");
  g.para(
    "The DML (Data Management List) tab tracks the SNS hierarchy and lets you tune " +
    "the per-DMC, per-ICN and per-PMC processing time. Six maintained SNS presets " +
    "are available: Generic, Aircraft (ATA-100), Helicopter, Land Vehicle, Surface " +
    "Ship and Munitions. Picking a preset seeds the system / sub-system / sub-" +
    "subsystem counts; you can override any of them. The DML cost reported here " +
    "is separate from data-module authoring and BREX cost — overhead and " +
    "contingency still apply."
  );

  g.h2("2.5 BR / BREX");
  g.para(
    "The Business Rules / BREX tab captures decision-point counts across 18 topical " +
    "categories at the S1000D issue you select (4.0, 4.1, 4.2, 5.0, 6.0). DP counts " +
    "are seeded from a maintained table that varies by issue — for example, " +
    "Common Constructs has 38 DPs in Issue 6.0 but only 32 in 4.0. Tick a category " +
    "to include its DPs in cost, or untick to exclude. Override the default DP count " +
    "when you have a more accurate figure."
  );
  g.bullets([
    "Set the 'define rule' and 'BREX path' minutes-per-DP to reflect your team's authoring pace.",
    "Edit the recommended-roles table to match your actual team mix.",
    "Tick 'Include in project total' to fold BREX cost into the headline grand total.",
    "Click 'Download BREX Skeleton' to generate a starter .brex.xml. Applicability blocks are pre-seeded from the SBS systems — see Chapter 6 for the full applicability story.",
  ]);

  // ---------- Chapter 3 ----------
  g.newPage();
  g.h1("3. Planning the work");

  g.h2("3.1 Roles & Rates");
  g.para(
    "Edit the nine default S1000D roles (Bid Manager, Project Manager, Author, " +
    "Illustrator, Reviewer, Subject Matter Expert, QA Lead, Publication Manager, " +
    "Configuration Manager) or add your own. Set an hourly rate and an effort " +
    "percentage for each role. The effort column must sum to 100 % — the Readiness " +
    "scorecard will warn you if it doesn't."
  );
  g.bullets([
    "Hourly rate × role share of total hours = per-role cost.",
    "Use the 'Cost Adjustments' panel to set Overhead % and Contingency % — applied on top of base cost.",
    "The blended rate shown in the footer is the cost-weighted average across all roles.",
    "Removing a role with non-zero effort redistributes that effort to 'unassigned' — re-balance before you save.",
  ]);

  g.h2("3.2 Rate Cards");
  g.para(
    "Rate cards bundle a complete set of currency, roles, rates, default DM hours, " +
    "overhead and contingency. They follow a lightweight approval workflow so " +
    "finance-approved rates stay separate from drafts."
  );
  await g.image("/guide-assets/ratecards.jpg", "the Rate Cards tab with status badges (draft / pending / approved / rejected) and per-row action buttons.");

  g.h3("Lifecycle");
  g.kbdTable([
    ["Draft", "Newly created or just edited. Editing any field bumps the version number and clears any approver/rejected metadata."],
    ["Pending", "Submitted for approval. Cannot be edited until approved or rejected."],
    ["Approved", "Locked. Editing the rate card automatically pushes it back to Draft, ready for re-approval — so an approved card always reflects what was approved."],
    ["Rejected", "An approver has knocked it back with a reason. The submitter can edit and re-submit."],
  ]);

  g.h3("Common actions");
  g.kbdTable([
    ["Apply", "Copies the rate card's rates, currency, overhead and contingency into the current estimate. Quantities and the matrix are kept."],
    ["Submit", "Moves a draft card into 'pending'. You will be prompted for your name as the submitter."],
    ["Approve", "Marks a pending card as approved. Records the approver's identity and timestamp."],
    ["Reject", "Sends the card back with a reason. The submitter can edit and re-submit."],
    ["History", "Inspect the full version history. Every save before approval is snapshotted with version number, role mix and rates."],
    ["Edit", "Open the editor for any rate card. Editing an approved card moves it back to Draft."],
    ["Delete", "Permanently removes the rate card and its history."],
  ]);
  g.callout("Note",
    "Until authentication is wired in, the submitter and approver are captured as " +
    "free-text. Treat the workflow as governance-light: the audit trail is preserved " +
    "(version + identity + timestamp), but the identity itself is not yet verified.");

  g.h2("3.3 Resourcing");
  g.para(
    "Convert project hours into headcount. Set the project window (start and end " +
    "date), pick a location preset for the weekend days and public-holiday count " +
    "(AU, US, UK, DE, FR, IN, JP and more), and configure standard and effective " +
    "hours per day. The output shows the working-day count, the required FTE and " +
    "per-role staffing demand."
  );
  g.bullets([
    "Standard hours per day — the contracted day length (8 by default).",
    "Effective hours per day — the productive hours after meetings, admin and breaks (6 by default).",
    "Weekend days — array of weekday numbers (0 = Sun, 6 = Sat).",
    "Public holidays — number of working days lost across the project window.",
    "FTE = total hours ÷ (effective hours per day × working days).",
  ]);

  g.h2("3.4 Schedule");
  g.para(
    "Spread project hours across the six standard S1000D phases (Plan, Author, " +
    "Illustrate, Review, Validate, Publish). For each phase set the percent " +
    "allocation, the start offset (in weeks from project start) and the duration. " +
    "When the resourcing start date is set, a month-by-month grid shows the load " +
    "profile."
  );
  g.bullets([
    "Choose the basis (Combined project + BREX, or Project only) at the top.",
    "Phase percentages should add up to 100 %. The footer warns you if they don't.",
    "Use the monthly grid to spot resourcing peaks before you commit to a delivery profile.",
    "Phases can overlap — set the start offset of a later phase before the end of the previous one if your team works in parallel.",
  ]);

  // ---------- Chapter 4 ----------
  g.newPage();
  g.h1("4. Preparing the bid");

  g.h2("4.1 Risk register");
  g.para(
    "Log risks with category (Technical, Schedule, Commercial, Resource, Quality, " +
    "External), likelihood and impact (1–5 each), mitigation and owner role. The " +
    "score (likelihood × impact) and band (LOW 1–6 / MED 7–12 / HIGH 13–19 / CRIT " +
    "20–25) are computed for you and shown as coloured badges."
  );
  g.bullets([
    "Capture assumptions and dependencies in their own tables — they feed three of the Readiness scorecard checks.",
    "Import a CSV or XLSX using the Risk Importer near the bottom of the tab. Three blocks are supported (RISKS, ASSUMPTIONS, DEPENDENCIES); each is header-aware.",
    "Turn on 'Auto-bump contingency' to let aggregate risk exposure drive a contingency uplift across the whole estimate, capped at the configured maximum percentage.",
    "The risk register rolls up into the Bid Pack ZIP export.",
  ]);

  g.h2("4.2 Bid Pricing");
  g.para(
    "Set the target margin (% above cost), win probability (%) and any strategic " +
    "adjustment (% up or down). The calculator returns a target price, an adjusted " +
    "price and an expected value (price × win probability). The competitor price " +
    "field gives a side-by-side delta and a textual recommendation: 'aggressive', " +
    "'neutral' or 'premium' depending on the gap."
  );

  g.h2("4.3 Summary");
  g.para(
    "A read-only dashboard. Eight stat cards show base cost, overhead, contingency, " +
    "grand total, blended rate, total hours, FTE and DM / illustration counts. Two " +
    "charts give a quick feel for the cost split: role-effort pie chart and DM / " +
    "illustration bar chart."
  );

  g.h2("4.4 Readiness scorecard");
  g.para(
    "The final go / no-go gate before you ship the bid. 14 checks across five " +
    "dimensions (Scope, Plan, Risk, Pricing, Pack) return pass, warning or fail. " +
    "The headline verdict is READY, WARNINGS or NOT READY, with a numeric score " +
    "out of 100 and the per-check breakdown."
  );
  await g.image("/guide-assets/readiness.jpg", "the Readiness tab — verdict card, 14-check list and the Seed Pack / Bid Pack export buttons.");

  g.h3("Pack exports");
  g.kbdTable([
    ["CSDB Seed Pack (ZIP)", "BREX skeleton + SNS XML + DMC inventory CSV + Readiness markdown + manifest.json with the applicabilityMap. Goal: stand up a fresh CSDB."],
    ["Bid Pack (ZIP)", "Everything in the seed pack plus the risk register, schedule and bid-pricing build-up. Goal: hand a reviewer the complete bid file."],
    ["Public share links", "Upload either pack to the server and copy a public URL. Anyone with the link can download it; the link does not expose your saved-projects list."],
  ]);
  g.callout("Tip",
    "The seed-pack manifest.json is self-referential. Every DMC carries an " +
    "applicPropertyValue (such as 'HYDRAULICS') that matches the applicPropertyValues " +
    "tokens in the BREX skeleton's <assert> blocks. Downstream tooling can join the " +
    "two without any string munging. The manifest also carries a top-level fxFallback " +
    "boolean — true when the chosen currency is outside the ECB live-rates set.");

  // ---------- Chapter 5 ----------
  g.newPage();
  g.h1("5. Library");

  g.h2("5.1 Compare");
  g.para(
    "Pick any two saved estimates side-by-side. The Compare tab lists per-row " +
    "differences and a grand-total delta. When the two estimates use different " +
    "currencies, switch on 'Convert B into A's currency' and the tab will fetch " +
    "the latest ECB rates (cached for six hours) to give an apples-to-apples view."
  );
  await g.image("/guide-assets/compare.jpg", "the Compare tab with the FX 1:1 fallback banner and the (1:1) marker on the Δ column.");
  g.h3("FX 1:1 fallback");
  g.para(
    "When one or both of the compared estimates use a currency outside the ECB " +
    "live-rates set, an amber banner appears above the diff table warning that " +
    "the conversion falls back to a 1:1 identity. The Δ column header gets a small " +
    "amber (1:1) tag, and the delta cells render in muted grey italics — a quiet " +
    "visual signal that the difference is unreliable in this mode."
  );
  g.callout("FX availability", "Live ECB rates cover 31 major currencies (USD, EUR, GBP, JPY, CHF, CNY, AUD, " +
    "CAD, NZD, BRL, MXN, SEK, NOK, DKK, ISK, PLN, CZK, HUF, RON, BGN, TRY, ILS, ZAR, " +
    "INR, HKD, SGD, KRW, MYR, IDR, PHP, THB). The other 79 currencies display fine, " +
    "but cross-currency Compare conversions fall back to 1:1 for them.", "amber");

  g.h2("5.2 Portfolio dashboard");
  g.para(
    "Roll-up across every saved estimate. Per-currency revenue totals, aggregated " +
    "role utilisation, tag distribution and a top-projects table with a currency " +
    "filter. Click any row to load that estimate into the editor. Stat cards at " +
    "the top show portfolio size, total revenue (in the chosen currency) and the " +
    "average estimate size."
  );

  g.h2("5.3 Saved Projects");
  g.para(
    "The home of every estimate you have ever saved. Filter by name or tag, load " +
    "any project with one click, duplicate to spawn a fresh copy, or open the " +
    "History modal to inspect and restore earlier revisions. The bin icon deletes " +
    "an estimate and its revision history permanently."
  );

  // ---------- Chapter 6 ----------
  g.newPage();
  g.h1("6. Currency and FX");

  g.h2("110 currencies");
  g.para(
    "The estimator supports 110 ISO 4217 currencies, grouped in the header " +
    "dropdown into two sets:"
  );
  g.bullets([
    "FX-supported (live rates) — 31 currencies with daily ECB rates via frankfurter.dev.",
    "All other currencies — 79 additional codes. Display, formatting and exports work normally; only the Compare tab's cross-currency conversion falls back to 1:1.",
  ]);

  g.h2("FX status pill");
  g.para(
    "Right of the currency selector sits a small status pill. Green dot + 'FX LIVE' " +
    "when the chosen currency has an ECB rate; grey dot + 'FX 1:1' when it does not. " +
    "Hover for an explanation."
  );

  g.h2("FX 1:1 in exports");
  g.para(
    "When the estimate uses a non-FX currency, the CSV, XLSX and PDF exports all " +
    "carry a short 'FX status' line warning that cross-currency comparisons fall " +
    "back to 1:1. The PDF renders this as an amber callout box above the metrics " +
    "table. The CSDB Seed Pack and Bid Pack manifests carry a machine-readable " +
    "fxFallback boolean for the same purpose."
  );

  // ---------- Chapter 7 ----------
  g.h1("7. Importing data");
  g.h2("DM matrix importer");
  g.para(
    "On the Data Modules tab, the 'Import' button accepts CSV or XLSX files with " +
    "the same column shape as the export. The first cell of each data row is the " +
    "DM code (zero-padded codes like 040 are preserved). Replace overwrites the " +
    "matrix; Merge adds quantities and refreshes hours."
  );
  g.h2("SBS importer");
  g.para(
    "On the System Breakdown Structure tab. Expected columns (case-insensitive): " +
    "System, Sub-System, Sub-Subsystem, General, Repairable. Blank Sub or Sub-Sub " +
    "cells mean the row applies to the parent level. The importer surfaces a 4-card " +
    "stats grid (rows applied, systems created, rows skipped, rows with zero qty) " +
    "and a collapsible amber warnings panel listing duplicate names, missing " +
    "headers and any malformed rows."
  );
  g.h2("Risk register importer");
  g.para(
    "On the Risk tab. Three blocks are supported (RISKS, ASSUMPTIONS, DEPENDENCIES), " +
    "each with its own header row. RISKS columns: id, title, category, likelihood, " +
    "impact, mitigation, owner. ASSUMPTIONS columns: id, text. DEPENDENCIES columns: " +
    "id, type, text (header-aware mapping)."
  );

  // ---------- Appendices ----------
  g.newPage();
  g.h1("Appendix A — Glossary");
  g.kbdTable([
    ["SBS", "System Breakdown Structure. The product decomposed into Systems, Sub-Systems and Sub-Subsystems."],
    ["SNS", "Standard Numbering System. The S1000D scheme for system / sub-system / sub-subsystem codes."],
    ["DM", "Data Module. The atomic unit of S1000D technical content."],
    ["DMC", "Data Module Code. The unique identifier for a DM (for example PROJECT-A-21-10-00-00-0001-040A-D)."],
    ["DML", "Data Management List. The register of every DMC, ICN and PMC in the project."],
    ["ICN", "Illustration Control Number — identifier for an illustration."],
    ["PMC", "Publication Module Code — identifier for a publication module."],
    ["BREX", "Business Rules Exchange. The XML data module that captures project-specific business rules."],
    ["BR", "Business Rule. A decision point recorded against a topical category."],
    ["DP", "Decision Point. One row of authoring effort against a BR category."],
    ["FTE", "Full-Time Equivalent. Headcount expressed in standard work-days per year."],
    ["Blended rate", "The cost-weighted average hourly rate across all roles."],
    ["Overhead %", "A multiplier on base cost to cover indirect cost (premises, tools, training, etc.)."],
    ["Contingency %", "A reserve added on top to cover known unknowns. Can be auto-bumped from the risk register."],
    ["Seed Pack", "A ZIP bundle of starter CSDB artefacts (BREX skeleton, SNS XML, DMC inventory, readiness scorecard, manifest)."],
    ["Bid Pack", "A larger ZIP that adds the risk register, schedule and bid pricing to the seed pack."],
    ["FX 1:1 fallback", "Identity conversion used by the Compare tab when one side's currency has no live ECB rate."],
    ["Applicability map", "A keyed lookup in the seed-pack manifest that groups DMCs by applicPropertyValue (system token)."],
  ]);

  g.h1("Appendix B — Frequently asked questions");
  g.h3("Why does the total cost change when I switch currency?");
  g.para(
    "It doesn't. The numbers stay the same; only the currency symbol and " +
    "locale-aware number formatting change. To convert to another currency, use " +
    "the Compare tab's FX toggle.");
  g.h3("Where is my data stored?");
  g.para(
    "Every estimate, rate card and share link is stored in the MongoDB database " +
    "that runs alongside the application. Nothing is sent to a third party — " +
    "apart from a 6-hourly call to frankfurter.dev for ECB exchange rates.");
  g.h3("Can I work on more than one estimate at the same time?");
  g.para(
    "Yes. Save the first one, click 'New' or 'Duplicate' to clear the workspace " +
    "or spawn a copy, and start the next. The Saved Projects list keeps them " +
    "tidy and the Compare tab puts them side-by-side.");
  g.h3("How do I delete an estimate I no longer need?");
  g.para(
    "Open Library → Saved Projects, locate the row, and click the bin icon. " +
    "The estimate and its revision history are removed permanently.");
  g.h3("Where do I find the latest exchange rates?");
  g.para(
    "The Compare tab fetches them directly from the European Central Bank via " +
    "frankfurter.dev and caches them for six hours. If the service is unreachable, " +
    "the conversion falls back to 1.0 and you'll see a small warning.");
  g.h3("Why is my Compare delta showing in grey italics?");
  g.para(
    "Because one or both of the compared estimates uses a currency outside the " +
    "ECB live-rates set. The Δ column header carries an amber (1:1) tag and the " +
    "delta cells are desaturated — a visual signal that the difference is " +
    "unreliable. Switch one estimate to an FX-supported currency for an " +
    "apples-to-apples comparison.");
  g.h3("Can I download this user guide?");
  g.para(
    "Yes. Click the 'Guide' button in the top right, then 'PDF' inside the drawer. " +
    "This very document is the result.");

  g.h1("Appendix C — Tab cheat sheet");
  g.kbdTable([
    ["Scope · System Breakdown", "Tree editor, DMC inventory and spreadsheet importer. Drives the rest of the estimate."],
    ["Scope · Data Modules", "Quantity × hours matrix for every DM type and complexity level."],
    ["Scope · Illustrations", "Quantity × hours for Simple, Intermediate and Complex illustrations."],
    ["Scope · DML / SNS", "SNS preset and DMC / ICN / PMC counts."],
    ["Scope · BR / BREX", "Decision points by category, BREX role mix, BREX skeleton download."],
    ["Plan · Roles & Rates", "Nine roles, hourly rate, effort %, overhead and contingency."],
    ["Plan · Rate Cards", "Versioned, approvable rate-card library."],
    ["Plan · Resourcing", "Project window, working days, FTE calculation."],
    ["Plan · Schedule", "Six-phase effort allocation and monthly load grid."],
    ["Bid · Risk", "Risk register, assumptions, dependencies, auto-contingency uplift."],
    ["Bid · Bid Pricing", "Target margin, win probability, competitor price, recommendation."],
    ["Bid · Summary", "Dashboard with role-cost pie and DM / illustration bars."],
    ["Bid · Readiness", "Go / no-go scorecard, seed pack and bid pack exports, share links."],
    ["Library · Compare", "Side-by-side delta with FX conversion and 1:1 fallback signalling."],
    ["Library · Portfolio", "Roll-up across every saved estimate."],
    ["Library · Saved Projects", "Load, duplicate, delete and restore revisions."],
  ]);

  g.finalise("S1000D_Cost_Estimator_User_Guide.pdf");
}
